import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import {t} from 'i18next'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { Badge, Button } from 'reactstrap'

import { headersState } from '../../../recoil/recoil'
import { isNull, paginationComponentOptions } from '../../../izUtils'
import { axiosError } from '../../../helpers/response'
import Loader from '../../spinner/Loader'
import { displayValue, entityName, importTableStyles, WarningList } from './importHelpers'

const statuses = ['changed', 'new', 'unresolved', 'unchanged', 'ignored'];
const statusColors = { new: 'success', changed: 'primary', unresolved: 'danger', unchanged: 'secondary', ignored: 'light' };

const RowComparison = ({data, importId, apiUrl}) => {
    const Navigate = useNavigate();
    const headers = useRecoilValue(headersState);
    const [comparison, setComparison] = useState(null)
    const [onlyDifferences, setOnlyDifferences] = useState(false)

    useEffect(() => {
        axios.get(apiUrl + '/' + importId + '/rows/' + data.row_number, {headers}).then(response => {
            setComparison(response.data.row.comparison);
        }).catch(error => axiosError(error, Navigate));
    }, [importId, data.row_number]) // eslint-disable-line react-hooks/exhaustive-deps

    const entries = isNull(comparison) ? [] : comparison.filter(entry => !onlyDifferences || entry.differs);

    return (
        <div className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
            <WarningList warnings={data.warnings} />
            {isNull(comparison) ? <div style={{ position: 'relative', height: '60px' }}><Loader /></div> : (
                <>
                    <Button color="secondary" size="sm" className="my-2" onClick={() => setOnlyDifferences(!onlyDifferences)}>
                        {t(onlyDifferences ? 'contracts.import.compare.showAll' : 'contracts.import.compare.onlyDifferences')}
                    </Button>
                    <table className="table table-sm table-bordered mb-0 contract-import-comparison" style={{ backgroundColor: '#fff' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '70px' }}>{t('contracts.import.column')}</th>
                                <th>{t('contracts.import.compare.excelField')}</th>
                                <th>{t('contracts.import.record')}</th>
                                <th>{t('contracts.import.compare.excel')}</th>
                                <th>{t('contracts.import.compare.database')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map(entry => (
                                <tr key={'column-' + entry.column} style={entry.differs ? { backgroundColor: '#fff3cd' } : {}}>
                                    <td>{entry.column}</td>
                                    <td>{entry.header}</td>
                                    <td>{entityName(entry.entity)}</td>
                                    <td>{entry.differs ? <b>{entry.excel}</b> : entry.excel}</td>
                                    <td>{displayValue(entry.database)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    )
}

const ImportRows = ({importData, apiUrl}) => {
    const Navigate = useNavigate();
    const headers = useRecoilValue(headersState);

    const [rows, setRows] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [status, setStatus] = useState('changed')
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(50)

    useEffect(() => {
        setLoading(true);

        axios.get(apiUrl + '/' + importData.id + '/rows', {headers, params: {status, page, per_page: perPage}}).then(response => {
            setRows(response.data.rows.data);
            setTotal(response.data.rows.total);
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            axiosError(error, Navigate);
        });
    }, [importData.id, status, page, perPage]) // eslint-disable-line react-hooks/exhaustive-deps

    const columns = useMemo(() => {
        const list = [
            {
                name: t('contracts.import.table.row'),
                width: '90px',
                cell: row => row.row_number,
            },
            {
                name: t('contracts.import.table.status'),
                width: '140px',
                cell: row => <Badge color={statusColors[row.status]} className={row.status === 'ignored' ? 'text-dark' : ''}>{t('contracts.import.rowStatus.' + row.status)}</Badge>,
            },
            {
                name: t('table.contract_number'),
                wrap: true,
                cell: row => row.label.contract_number,
            },
            {
                name: t('table.client'),
                wrap: true,
                cell: row => row.label.client,
            },
            {
                name: t('table.facility'),
                wrap: true,
                cell: row => row.label.facility,
            },
            {
                name: t('contracts.import.table.system'),
                wrap: true,
                cell: row => row.label.security_system,
            },
            {
                name: t('contracts.import.table.warnings'),
                width: '120px',
                cell: row => {
                    const review = row.warnings.filter(warning => warning.level === 'review').length;
                    return <span className={review !== 0 ? 'text-danger fw-bold' : ''}>{row.warnings.length}</span>
                },
            },
        ];

        if (importData.status !== 'analyzed') {
            list.push({
                name: t('contracts.import.table.result'),
                width: '150px',
                cell: row => isNull(row.result) ? '' : t('contracts.import.rowResult.' + row.result, {defaultValue: row.result}),
            });
        }

        return list;
    }, [importData.status])

    const counts = !isNull(importData.summary) ? importData.summary.rows : {};
    const detailProps = useMemo(() => ({importId: importData.id, apiUrl}), [importData.id, apiUrl]);

    return (
        <div>
            <div className="mb-3" id="contract-import-statuses">
                {statuses.map(item => (
                    <Button
                        key={'status-' + item}
                        color={status === item ? 'primary' : 'light'}
                        size="sm"
                        className="me-2 mb-2"
                        onClick={() => { setPage(1); setStatus(item); }}
                    >
                        {t('contracts.import.rowStatus.' + item)} ({counts[item] || 0})
                    </Button>
                ))}
            </div>

            <div style={{ position: 'relative' }}>
                {loading && <Loader />}
                <DataTable
                    key={status + importData.id}
                    columns={columns}
                    data={rows}
                    keyField="id"
                    customStyles={importTableStyles}
                    expandableRows
                    expandableRowsComponent={RowComparison}
                    expandableRowsComponentProps={detailProps}
                    pagination
                    paginationServer
                    paginationTotalRows={total}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[25, 50, 100, 200]}
                    paginationComponentOptions={paginationComponentOptions(t)}
                    onChangePage={value => setPage(value)}
                    onChangeRowsPerPage={(value, currentPage) => { setPerPage(value); setPage(currentPage); }}
                    noDataComponent={t('contracts.import.noRows')}
                />
            </div>
        </div>
    )
}

export default ImportRows
